import { useSyncExternalStore } from 'react';
import {
  accountStatusVersion,
  accountStreamStatus,
  arrivalCount,
  arrivalsVersion,
  hasStreamGap,
  subscribeAccountStatus,
  subscribeArrivals,
  subscribeStreamGap,
  streamGapVersion,
  type AccountStreamStatus,
} from './arrivals';

/**
 * React's readers for the two stream-written stores.
 *
 * `useSyncExternalStore`, not a `useState` in an effect: the stream writes from
 * outside React, and the effect version tears during a concurrent render and
 * costs an extra commit per event. It also keeps these subscriptions off the
 * `set-state-in-effect` warning list that `eslint.config.js` documents.
 *
 * Both snapshots are a **version number** rather than the value. `getSnapshot`
 * must return something reference-stable between changes, and a `Set` or a
 * record rebuilt per call loops forever; the version is compared, the value is
 * read once the version moved.
 */

export function useArrivalCount(
  accountId: string | undefined,
  folderId: string | undefined,
): number {
  useSyncExternalStore(subscribeArrivals, arrivalsVersion, arrivalsVersion);
  return arrivalCount(accountId, folderId);
}

/**
 * The same count over several mailboxes, for the unified view's pill (ADR 0056).
 *
 * The store is already keyed `(account, folder)`, so a merged list is a sum
 * rather than a second store. The rule it preserves is the important one: an
 * arrival still waits behind the pill instead of splicing itself into a
 * virtualized list the user is scrolled into — which is also what keeps the
 * merge's ordering guarantee true under live mail.
 */
export function useUnifiedArrivalCount(
  folders: readonly { accountId: string; folderId: string }[],
): number {
  useSyncExternalStore(subscribeArrivals, arrivalsVersion, arrivalsVersion);
  return folders.reduce(
    (total, { accountId, folderId }) => total + arrivalCount(accountId, folderId),
    0,
  );
}

/**
 * Whether the live stream was interrupted and may have missed changes.
 *
 * Unlike an arrival count this names nothing, so a view cannot splice it in —
 * it can only refetch. Which is why the decision of *when* belongs to the view
 * (see `MailView`): quietly when nothing is selected, open or scrolled, and
 * through the pill otherwise.
 */
export function useStreamGap(): boolean {
  useSyncExternalStore(subscribeStreamGap, streamGapVersion, streamGapVersion);
  return hasStreamGap();
}

export function useAccountStreamStatus(
  accountId: string | undefined,
): AccountStreamStatus | undefined {
  useSyncExternalStore(subscribeAccountStatus, accountStatusVersion, accountStatusVersion);
  return accountStreamStatus(accountId);
}
